// Canonical product analytics events (PostHog source of truth).
// Keep this list small: one name per user-visible action. Ids only as *_hash.
// Never attach email, name, DOB, gender, ethnicity, notes, PIN, prompt text,
// appearance, diagnosis, SPD, image bytes, or Stripe customer PII.

export const PRODUCT_EVENTS = {
  therapist_signup: 'therapist_signup',
  therapist_login: 'therapist_login',
  therapist_login_failed: 'therapist_login_failed',
  client_created: 'client_created',
  session_started: 'session_started',
  image_generate_requested: 'image_generate_requested',
  image_generate_completed: 'image_generate_completed',
  image_saved: 'image_saved',
  image_exported: 'image_exported',
  billing_checkout_started: 'billing_checkout_started',
  billing_checkout_completed: 'billing_checkout_completed',
  error_occurred: 'error_occurred',
} as const

export type ProductEventName = (typeof PRODUCT_EVENTS)[keyof typeof PRODUCT_EVENTS]

export const PRODUCT_EVENT_NAMES: ProductEventName[] = Object.values(PRODUCT_EVENTS)

const GENERATE_PHI_KEYS = [
  'age',
  'gender',
  'ethnicity',
  'diagnosis',
  'spd',
  'spdsubtype',
  'prompt',
  'originalprompt',
  'customizationoptions',
  'sensorymodality',
] as const

const GENERATE_PHI_KEY_SET = new Set<string>(GENERATE_PHI_KEYS)

function omitPhiKeys(input: Record<string, unknown>): Record<string, unknown> {
  const out: Record<string, unknown> = {}
  for (const [key, value] of Object.entries(input)) {
    const normalized = key.toLowerCase().replace(/[_-]/g, '')
    if (GENERATE_PHI_KEY_SET.has(normalized)) {
      continue
    }
    out[key] = value
  }
  return out
}

export function imageGenerateRequestedParams(input: {
  prompt_length: number
  style?: string
  client_id_hash?: string
}): Record<string, unknown> {
  return omitPhiKeys({
    prompt_length: input.prompt_length,
    style: input.style,
    client_id_hash: input.client_id_hash,
  })
}

export function imageGenerateCompletedParams(input: {
  success: boolean
  duration_ms?: number
  error_code?: string
}): Record<string, unknown> {
  return omitPhiKeys({
    success: input.success,
    duration_ms: input.duration_ms,
    error_code: input.error_code,
  })
}

export function errorOccurredParams(input: {
  surface: string
  error_code: string
}): Record<string, unknown> {
  return {
    surface: input.surface,
    error_code: input.error_code,
  }
}
